import './LogisticAddressFormSection.scss';

import { LoadingOutlined } from '@ant-design/icons';
import { Alert, Button, Form, Input, Table, Tag } from 'antd';
import { FormProps } from 'antd/lib';
import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import AntdSelect from '@/components/general/AntdSelect/AntdSelect';
import { useLogisticAddressForm } from '@/hooks/useLogisticAddressForm';
import { isValidPhoneNumber, phoneNumberMeta } from '@/utils/phoneNumbers';

/**
 * Sección de formulario de dirección logística unificada.
 * Reemplaza a ShipmentAddressForm y ReceptionReturnAddressForm,
 * que eran >90% idénticos.
 *
 * metadata:
 * - feature: logistic.address-form-section
 * - purpose: unifica ShipmentAddressForm y ReceptionReturnAddressForm en un solo componente genérico
 *   que acepta campos extra al inicio (prependFields) y permite configurar la clase CSS de las filas
 */
interface LogisticAddressFormSectionProps {
  formProps: FormProps;
  initialValuesModified?: any;
  setSaveAddress?: (value: boolean) => void;
  initialValuesSetted?: boolean;
  /** Clase CSS para las filas de campos (ej: "ShipmentFormRow", "ReceptionFormRow") */
  rowClassName?: string;
  /** Campos extra que se renderizan antes de la tabla de direcciones guardadas */
  beforeTableFields?: ReactNode;
  /** Campos extra que se renderizan después de la tabla y antes de los campos de ubicación */
  afterTableFields?: ReactNode;
}

const LogisticAddressFormSection = ({
  formProps,
  initialValuesModified,
  setSaveAddress,
  initialValuesSetted,
  rowClassName = 'LogisticAddressFormRow',
  beforeTableFields,
  afterTableFields,
}: LogisticAddressFormSectionProps) => {
  const { t } = useTranslation();
  const {
    company,
    pais,
    provincia,
    selectedAddress,
    tableProps,
    dataSource,
    paisesSelectProps,
    provinciasSelectProps,
    localidadesSelectProps,
    countryCode,
    isBrasilSelected,
    localidadesOptions,
    handleSavedAddressChange,
    handleLocalidadChange,
    cepLookupState,
    enableBrazilManualLocation,
  } = useLogisticAddressForm({
    formProps,
    initialValuesModified,
    initialValuesSetted,
    setSaveAddress,
  });
  const postalCodeLabel = isBrasilSelected ? 'CEP' : t('Código postal');
  const canEditLocation = !isBrasilSelected || cepLookupState.canEditLocationManually;
  const canEditAddress = !isBrasilSelected || cepLookupState.canEditAddressManually;

  return (
    <div className="LogisticAddressForm">
      {beforeTableFields}

      {!!company && dataSource && dataSource.length > 0 && (
        <Table
          {...tableProps}
          className="LogisticAddressFormTable"
          size="small"
          rowKey="id"
          scroll={{ x: 500, y: 150 }}
          pagination={false}
          rowSelection={{
            type: 'radio',
            selectedRowKeys: selectedAddress?.id ? [selectedAddress.id] : [],
            onChange: handleSavedAddressChange,
          }}>
          <Table.Column dataIndex="description" title={t('Direcciones guardadas')} />
        </Table>
      )}

      {afterTableFields}

      <div className={rowClassName}>
        <Form.Item label={t('País')} name={'receiver_pais'}>
          <AntdSelect {...paisesSelectProps} allowClear showSearch disabled={isBrasilSelected} />
        </Form.Item>
        {isBrasilSelected ? (
          <Form.Item
            label={postalCodeLabel}
            name={'receiver_postal_code'}
            rules={[
              {
                required: true,
                message: t('El {{field}} es requerido', { field: postalCodeLabel }),
              },
            ]}>
            <Input suffix={cepLookupState.loading && <LoadingOutlined />} />
          </Form.Item>
        ) : (
          <Form.Item label={t('Provincia')} name={'receiver_provincia'}>
            <AntdSelect {...provinciasSelectProps} disabled={!pais} allowClear />
          </Form.Item>
        )}
      </div>
      {isBrasilSelected && cepLookupState.error && (
        <Alert
          style={{ marginBottom: 8 }}
          type="warning"
          showIcon
          message={t('No se pudo encontrar una dirección para el CEP ingresado')}
          action={
            !canEditLocation ? (
              <Button size="small" onClick={enableBrazilManualLocation}>
                {t('Cargar manualmente')}
              </Button>
            ) : (
              <Tag color="warning">{t('Carga manual')}</Tag>
            )
          }
        />
      )}
      <div className={rowClassName}>
        {isBrasilSelected && (
          <Form.Item label={'UF'} name={'receiver_provincia'}>
            <AntdSelect {...provinciasSelectProps} disabled={!canEditLocation} allowClear />
          </Form.Item>
        )}
        <Form.Item
          label={isBrasilSelected ? t('Municipio') : t('Localidad')}
          name={'receiver_localidad'}
          rules={[
            {
              required: true,
              message: t(
                isBrasilSelected ? 'El {{field}} es requerido' : 'El {{field}} es requerido',
                {
                  field: isBrasilSelected ? t('Municipio') : t('Localidad'),
                },
              ),
            },
          ]}>
          <AntdSelect
            {...localidadesSelectProps}
            options={localidadesOptions}
            onChange={handleLocalidadChange}
            disabled={!pais || !provincia || !canEditLocation}
            allowClear
            showSearch
          />
        </Form.Item>
        {!isBrasilSelected && (
          <Form.Item
            label={postalCodeLabel}
            name={'receiver_postal_code'}
            rules={[
              {
                required: true,
                message: t('El {{field}} es requerido', { field: postalCodeLabel }),
              },
            ]}>
            <Input />
          </Form.Item>
        )}
      </div>
      <div className={rowClassName}>
        <Form.Item
          label={t('Dirección')}
          name={'receiver_address'}
          rules={[
            {
              required: true,
              message: t('La {{field}} es requerida', { field: t('dirección') }),
            },
          ]}>
          <Input disabled={!canEditAddress} />
        </Form.Item>
        {isBrasilSelected && (
          <>
            <Form.Item
              label={t('Número')}
              name={'receiver_address_number'}
              rules={[
                {
                  required: true,
                  message: t('El {{field}} es requerido', { field: t('número') }),
                },
              ]}>
              <Input />
            </Form.Item>
            <Form.Item label={t('Complemento')} name={'receiver_address_complement'}>
              <Input />
            </Form.Item>
          </>
        )}
      </div>

      <div className={rowClassName}>
        <Form.Item
          label={t('Email')}
          name={'receiver_email'}
          rules={[
            { type: 'email', message: t('Email inválido') },
            { required: true, message: t('El {{field}} es requerido', { field: t('email') }) },
          ]}>
          <Input />
        </Form.Item>
        <Form.Item
          tooltip={phoneNumberMeta[countryCode]?.helpText}
          label={t('Número de Teléfono')}
          name={'receiver_phone'}
          validateDebounce={1000}
          rules={[
            {
              required: true,
              message: t('El {{field}} es requerido', { field: t('número de teléfono') }),
            },
            {
              validator: (_, value) => {
                if (!value || !countryCode) {
                  return Promise.resolve();
                }

                if (!isValidPhoneNumber(countryCode, value)) {
                  return Promise.reject(
                    new Error(t('Número de teléfono inválido para el país seleccionado')),
                  );
                }

                return Promise.resolve();
              },
            },
          ]}>
          <Input prefix={phoneNumberMeta[countryCode]?.phoneCountry} />
        </Form.Item>
      </div>
      <div className={rowClassName}>
        <Form.Item
          label={t('Nombre y apellido del destinatario')}
          name={'receiver_name'}
          rules={[
            {
              required: true,
              message: t('El {{field}} es requerido', {
                field: t('Nombre y apellido del destinatario'),
              }),
            },
          ]}>
          <Input />
        </Form.Item>
      </div>
      <div className={rowClassName}>
        <Form.Item
          label={t('Instrucciones para la entrega')}
          tooltip={t('Se imprimirá en la etiqueta de envío')}
          name={'receiver_delivery_instructions'}>
          <Input />
        </Form.Item>
      </div>
    </div>
  );
};

export default LogisticAddressFormSection;
